import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import {
  OneTimeLink, PortalActivity, PortalActivityFilter, PortalAdminService, PortalContact, PortalSettings, PortalUpload,
} from '../../../services/portalAdmin.service';
import { saveBlob } from '../../../utils/saveBlob';
import { formatDateTime } from '../../../utils/dateUtils';
import { humanizeEnum } from '../../../utils/format';
import OneTimeLinkDialog from './OneTimeLinkDialog';

// ---- P1-B customer portal: the "Portal access" card on the customer page ----
const EVENTS = [
  'LOGIN_OK', 'LOGIN_FAILED', 'LOGIN_LOCKED', 'LOGOUT', 'INVITE_CREATED', 'RESET_CREATED', 'INVITE_ACCEPTED', 'PASSWORD_RESET',
  'PASSWORD_CHANGED', 'CONTACT_DISABLED', 'CONTACT_ENABLED', 'UNLOCKED', 'DOC_LIST', 'DOC_VIEW', 'PDF_DOWNLOAD',
  'STATEMENT_DOWNLOAD', 'PAY_LINK_CREATED', 'UPLOAD', 'UPLOAD_REVIEWED',
];
const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default'> = { ACTIVE: 'success', INVITED: 'warning', DISABLED: 'default' };
const isLocked = (c: PortalContact) => !!c.lockedUntil && new Date(c.lockedUntil).getTime() > Date.now();

const CustomerPortalCard: React.FC<{ customerId: string }> = ({ customerId }) => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<PortalSettings | null>(null);
  const [contacts, setContacts] = useState<PortalContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState<OneTimeLink | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [deactivating, setDeactivating] = useState<PortalContact | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // activity
  const [filter, setFilter] = useState<PortalActivityFilter>({});
  const [activity, setActivity] = useState<PortalActivity[]>([]);
  const [actLoading, setActLoading] = useState(false);
  // uploads
  const [uploads, setUploads] = useState<PortalUpload[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, c] = await Promise.all([PortalAdminService.getSettings(institutionId), PortalAdminService.contacts(institutionId, customerId)]);
      setSettings(s); setContacts(c);
    } catch (e) {
      toast.showApiError(e, 'Failed to load portal access');
    } finally {
      setLoading(false);
    }
  }, [institutionId, customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const loadActivity = useCallback(async () => {
    try {
      setActLoading(true);
      setActivity((await PortalAdminService.activity(institutionId, customerId, filter)).rows);
    } catch (e) {
      toast.showApiError(e, 'Failed to load portal activity');
    } finally {
      setActLoading(false);
    }
  }, [institutionId, customerId, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadUploads = useCallback(async () => {
    try { setUploads(await PortalAdminService.uploads(institutionId, customerId)); }
    catch (e) { toast.showApiError(e, 'Failed to load portal uploads'); }
  }, [institutionId, customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 1) loadActivity(); }, [tab, loadActivity]);
  useEffect(() => { if (tab === 2) loadUploads(); }, [tab, loadUploads]);

  const act = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try { await fn(); if (ok) toast.showSuccess(ok); await load(); }
    catch (e) { toast.showApiError(e, fail); }
    finally { setBusy(false); }
  };
  const linkAct = async (fn: () => Promise<OneTimeLink>, fail: string) => {
    setBusy(true);
    try { setLink(await fn()); await load(); }
    catch (e) { toast.showApiError(e, fail); }
    finally { setBusy(false); }
  };

  const invite = () => linkAct(async () => {
    const r = await PortalAdminService.invite(institutionId, customerId, { name: inviteName.trim(), email: inviteEmail.trim() });
    setInviteOpen(false); setInviteName(''); setInviteEmail('');
    return r;
  }, 'Failed to invite the contact');

  const exportXlsx = async () => {
    try { saveBlob(await PortalAdminService.exportActivity(institutionId, customerId, filter), 'portal-activity.xlsx'); }
    catch (e) { toast.showApiError(e, 'Failed to export portal activity'); }
  };
  const setF = (patch: Partial<PortalActivityFilter>) => setFilter(f => ({ ...f, ...patch }));

  const off = settings && (!settings.serverConfigured || !settings.enabled);
  return (
    <Paper sx={{ p: 2, mb: 3 }} data-help-section="sales.portalCard.card">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Portal access</Typography>
        <Button variant="outlined" size="small" data-help="sales.portalCard.invite" disabled={!!off || busy} onClick={() => setInviteOpen(true)}>Invite contact</Button>
      </Box>
      {settings && !settings.serverConfigured && (
        <Alert severity="warning" sx={{ mt: 1 }}>The customer portal is not configured on this server.</Alert>
      )}
      {settings && settings.serverConfigured && !settings.enabled && (
        <Alert severity="info" sx={{ mt: 1 }}>The customer portal is switched off for this institution. Switch it on in Customer Portal settings.</Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Contacts" data-help="sales.portalCard.tab.contacts" /><Tab label="Activity" data-help="sales.portalCard.tab.activity" /><Tab label="Uploads" data-help="sales.portalCard.tab.uploads" />
      </Tabs>

      {tab === 0 && (loading ? <CircularProgress /> : (
        <TableContainer data-help-section="sales.portalCard.contacts">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell data-help="sales.portalCard.contacts.col.name">Name</TableCell><TableCell data-help="sales.portalCard.contacts.col.email">E-mail</TableCell><TableCell data-help="sales.portalCard.contacts.col.status">Status</TableCell>
              <TableCell data-help="sales.portalCard.contacts.col.lastLogin">Last login</TableCell><TableCell data-help="sales.portalCard.contacts.col.lockedUntil">Locked until</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {contacts.length === 0 && <TableRow><TableCell colSpan={6} align="center">No portal contacts yet.</TableCell></TableRow>}
              {contacts.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell><TableCell>{c.email}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(c.status)} color={STATUS_COLOR[c.status] ?? 'default'} /></TableCell>
                  <TableCell>{c.lastLoginAt ? formatDateTime(c.lastLoginAt) : '-'}</TableCell>
                  <TableCell>{isLocked(c) ? formatDateTime(c.lockedUntil as string) : '-'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {c.status === 'INVITED' && (
                      <Button size="small" data-help="sales.portalCard.action.inviteLink" disabled={busy} onClick={() => linkAct(() => PortalAdminService.reinvite(institutionId, c.id), 'Failed to create the invite link')}>Copy invite link</Button>
                    )}
                    {c.status === 'ACTIVE' && (
                      <Button size="small" data-help="sales.portalCard.action.resetLink" disabled={busy} onClick={() => linkAct(() => PortalAdminService.resetLink(institutionId, c.id), 'Failed to create the reset link')}>New reset link</Button>
                    )}
                    {isLocked(c) && (
                      <Button size="small" data-help="sales.portalCard.action.unlock" disabled={busy} onClick={() => act(() => PortalAdminService.unlock(institutionId, c.id), 'Contact unlocked', 'Failed to unlock')}>Unlock</Button>
                    )}
                    {c.status !== 'DISABLED' ? (
                      <Button size="small" color="error" data-help="sales.portalCard.action.deactivate" disabled={busy} onClick={() => { setReason(''); setDeactivating(c); }}>Deactivate</Button>
                    ) : (
                      <Button size="small" data-help="sales.portalCard.action.reactivate" disabled={busy} onClick={() => act(() => PortalAdminService.reactivate(institutionId, c.id), 'Contact reactivated', 'Failed to reactivate')}>Reactivate</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="sales.portalCard.activity.filters">
            <TextField select size="small" data-help="sales.portalCard.activity.contact" label="Contact" value={filter.contactId ?? ''} sx={{ flex: '1 1 200px', maxWidth: 280 }}
              onChange={e => setF({ contactId: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {contacts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" data-help="sales.portalCard.activity.event" label="Event" value={filter.event ?? ''} sx={{ flex: '1 1 200px', maxWidth: 280 }}
              onChange={e => setF({ event: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {EVENTS.map(ev => <MenuItem key={ev} value={ev}>{humanizeEnum(ev)}</MenuItem>)}
            </TextField>
            <TextField size="small" data-help="sales.portalCard.activity.from" type="date" label="From" value={filter.from ?? ''} InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 150px', maxWidth: 200 }} onChange={e => setF({ from: e.target.value })} />
            <TextField size="small" data-help="sales.portalCard.activity.to" type="date" label="To" value={filter.to ?? ''} InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 150px', maxWidth: 200 }} onChange={e => setF({ to: e.target.value })} />
            <Button variant="outlined" size="small" data-help="sales.portalCard.activity.export" onClick={exportXlsx}>Export XLSX</Button>
          </Box>
          {actLoading ? <CircularProgress /> : (
            <TableContainer data-help-section="sales.portalCard.activity"><Table size="small">
              <TableHead><TableRow><TableCell data-help="sales.portalCard.activity.col.when">When</TableCell><TableCell data-help="sales.portalCard.activity.col.event">Event</TableCell><TableCell data-help="sales.portalCard.activity.col.contact">Contact</TableCell><TableCell data-help="sales.portalCard.activity.col.document">Document</TableCell><TableCell data-help="sales.portalCard.activity.col.ip">IP</TableCell><TableCell data-help="sales.portalCard.activity.col.detail">Detail</TableCell></TableRow></TableHead>
              <TableBody>
                {activity.length === 0 && <TableRow><TableCell colSpan={6} align="center">No activity.</TableCell></TableRow>}
                {activity.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{formatDateTime(a.occurredAt)}</TableCell><TableCell>{humanizeEnum(a.event)}</TableCell>
                    <TableCell>{a.contactName ?? '-'}</TableCell><TableCell>{a.documentType ? humanizeEnum(a.documentType) : '-'}</TableCell>
                    <TableCell>{a.ip ?? '-'}</TableCell><TableCell>{a.detail ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )}
        </Box>
      )}

      {tab === 2 && (
        <TableContainer data-help-section="sales.portalCard.uploads"><Table size="small">
          <TableHead><TableRow><TableCell data-help="sales.portalCard.uploads.col.file">File</TableCell><TableCell data-help="sales.portalCard.uploads.col.contact">Contact</TableCell><TableCell data-help="sales.portalCard.uploads.col.note">Note</TableCell><TableCell data-help="sales.portalCard.uploads.col.uploaded">Uploaded</TableCell><TableCell data-help="sales.portalCard.uploads.col.status">Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {uploads.length === 0 && <TableRow><TableCell colSpan={6} align="center">No uploads.</TableCell></TableRow>}
            {uploads.map(u => (
              <TableRow key={u.id}>
                <TableCell>{u.originalName}</TableCell><TableCell>{u.contactName ?? '-'}</TableCell><TableCell>{u.note ?? ''}</TableCell>
                <TableCell>{formatDateTime(u.uploadedAt)}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(u.status)} color={u.status === 'REVIEWED' ? 'success' : 'warning'} /></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" data-help="sales.portalCard.uploads.download" onClick={async () => {
                    try { saveBlob(await PortalAdminService.downloadUpload(institutionId, u.id), u.originalName); }
                    catch (e) { toast.showApiError(e, 'Failed to download the file'); }
                  }}>Download</Button>
                  {u.status === 'NEW' && (
                    <Button size="small" data-help="sales.portalCard.uploads.review" onClick={async () => {
                      try { await PortalAdminService.reviewUpload(institutionId, u.id); await loadUploads(); }
                      catch (e) { toast.showApiError(e, 'Failed to mark reviewed'); }
                    }}>Mark reviewed</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer>
      )}

      {inviteOpen && (
        <Dialog open onClose={() => !busy && setInviteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Invite portal contact</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField data-help="sales.portalCard.invite.name" label="Name" required fullWidth value={inviteName} inputProps={{ maxLength: 150 }} onChange={e => setInviteName(e.target.value)} />
              <TextField data-help="sales.portalCard.invite.email" label="E-mail" type="email" required fullWidth value={inviteEmail} inputProps={{ maxLength: 254 }} onChange={e => setInviteEmail(e.target.value)} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.portalCard.invite.cancel" onClick={() => setInviteOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="contained" data-help="sales.portalCard.invite.confirm" disabled={busy || !inviteName.trim() || !inviteEmail.includes('@')} onClick={invite}>Invite</Button>
          </DialogActions>
        </Dialog>
      )}
      {deactivating && (
        <Dialog open onClose={() => setDeactivating(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Deactivate {deactivating.name}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>Their sessions end now and they cannot sign in until you reactivate them.</Typography>
            <TextField data-help="sales.portalCard.deactivate.reason" label="Reason" required fullWidth multiline minRows={2} value={reason} inputProps={{ maxLength: 300 }} onChange={e => setReason(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.portalCard.deactivate.cancel" onClick={() => setDeactivating(null)}>Cancel</Button>
            <Button color="error" variant="contained" data-help="sales.portalCard.deactivate.confirm" disabled={busy || !reason.trim()} onClick={async () => {
              const c = deactivating;
              await act(() => PortalAdminService.deactivate(institutionId, c.id, reason.trim()), 'Contact deactivated', 'Failed to deactivate');
              setDeactivating(null);
            }}>Deactivate</Button>
          </DialogActions>
        </Dialog>
      )}
      {link && <OneTimeLinkDialog link={link} onClose={() => setLink(null)} />}
    </Paper>
  );
};

export default CustomerPortalCard;
// ---- end P1-B ----
